import { isDefined } from "@clipboard-health/util-ts";
import {
  shouldShowStillBookableChip,
  shouldShowUrgentChip,
} from "@src/appV2/Calendar/ShiftChips/utils";
import { UrgencyType } from "@src/appV2/Shifts/types";
import constate from "constate";
import { addHours, addMinutes, isAfter, isBefore, subMinutes } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import { capitalize, isNil } from "lodash";
import pluralize from "pluralize";

import { TripStatus } from "../../../containers/facilityDashboard/ShiftCalendar/dayView/workerETA/types";
import { determineIsWorkerAtHome, hasActuallyArrived } from "../../../utils/worker-eta";
import { ShiftAction, type ShiftBoardShift, type ShiftBoardWorkplace } from "../types";

const METERS_PER_MILE = 1609.34;

interface UseShiftCardProps {
  shift: ShiftBoardShift;
  workplace: ShiftBoardWorkplace;
}

const COLORS = {
  LIGHT_GREEN: { backgroundColor: "#DAFFCC", color: "#005E10" },
  GREEN: { backgroundColor: "#CFFFCC", color: "#005E06" },
  BLUE: { backgroundColor: "#CCDCFF", color: "#002C5E" },
  GOLD: { backgroundColor: "#E7DFA0", color: "#544A01" },
  BEIGE: { backgroundColor: "#FFEACC", color: "#5E4300" },
  GRAY: { backgroundColor: "#E2E2E2", color: "#535353" },
  RED: { backgroundColor: "#FFCCCC", color: "#8B0000" },
} as const;
type Color = (typeof COLORS)[keyof typeof COLORS];

const TIME_FORMAT = "h:mm a";

type ShiftStatus =
  | "Starts in 2+ hours"
  | "Urgent shift"
  | "Worker at home"
  | "On the way"
  | "Booked"
  | "Looking for worker"
  | "Looking for replacement"
  | "Canceled"
  | "Arrived"
  | "Clocked in"
  | "Clocked out"
  | "Never arrived"
  | "Shift completed"
  | "Unfilled"
  | "WLE"
  | "WLE RW";

function isUrgentShiftBookable(shift: ShiftBoardShift) {
  const currentTime = new Date();
  return (
    shift.urgency === UrgencyType.NCNS &&
    !shift.urgentlyBooked &&
    isBefore(currentTime, subMinutes(shift.end, 90)) &&
    ((shift.extendBookabilityTo && isBefore(currentTime, shift.extendBookabilityTo)) ??
      isBefore(currentTime, shift.start))
  );
}

function getShiftCancelledDetails(
  shift: ShiftBoardShift,
  workplace: ShiftBoardWorkplace
): string | undefined {
  const logs = shift.logs?.filter(
    (log) =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      log.action === ShiftAction.FACILITY_CANCEL ||
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      log.action === ShiftAction.FACILITY_CANCEL_FILLED
  );
  if (!logs || logs.length === 0) {
    return undefined;
  }

  const latestLog = logs[0];
  const formattedTime = latestLog.eventAt
    ? formatInTimeZone(latestLog.eventAt, workplace.timeZone, TIME_FORMAT)
    : undefined;
  const atFormattedTime = formattedTime ? `at ${formattedTime}` : "";

  if (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    latestLog.action === ShiftAction.FACILITY_CANCEL ||
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    latestLog.action === ShiftAction.FACILITY_CANCEL_FILLED
  ) {
    if (latestLog.performedBy?.type === "FACILITY") {
      return `${latestLog.performedBy.name} canceled the shift ${atFormattedTime}`;
    }

    return `Canceled per workplace's instructions ${atFormattedTime}`;
  }

  return `Shift canceled ${atFormattedTime}`;
}

function getWorkerDeclaredEtaMessageText(
  workerDeclaredEta: Date,
  timeZone: string
): string | undefined {
  return `Worker confirmed they would arrive around ${formatInTimeZone(
    workerDeclaredEta,
    timeZone,
    TIME_FORMAT
  )}.`;
}

function getEtaMessageText({
  shift,
  workplace,
  isUrgentShift,
}: {
  shift: ShiftBoardShift;
  workplace: ShiftBoardWorkplace;
  isUrgentShift: boolean;
}): string | undefined {
  if (!shift.worker) {
    return undefined;
  }

  const isUrgentlyBooked = shift.urgentlyBooked && isUrgentShift;

  if (
    !shift.trip ||
    [TripStatus.TRIP_CANCELLED, TripStatus.TRIP_EXPIRED].includes(shift.trip.status)
  ) {
    return `We&apos;re still waiting on ${shift.worker.name}&apos;s ETA`;
  }

  if (shift.trip) {
    const { workerDeclaredEta, initialEta, eta, status, isLate } = shift.trip;
    const useInitialEta =
      (status === TripStatus.TRIP_PENDING || status === TripStatus.TRIP_STARTED) &&
      eta.durationInMin === 0;
    const etaDurationInMinutes = useInitialEta ? initialEta.durationInMin : eta.durationInMin;

    if (isNil(etaDurationInMinutes)) {
      return `We&apos;re still waiting on ${shift.worker.name}&apos;s ETA`;
    }

    if (isDefined(workerDeclaredEta)) {
      return `${shift.worker.name} updated their ETA to ${formatInTimeZone(
        workerDeclaredEta,
        workplace.timeZone,
        TIME_FORMAT
      )}.`;
    }

    const arrivalTime = addMinutes(new Date(), etaDurationInMinutes);
    const workerDeclaredEtaMessageText = workerDeclaredEta
      ? `${getWorkerDeclaredEtaMessageText(workerDeclaredEta, workplace.timeZone)} Currently, `
      : undefined;

    return isLate && !isUrgentlyBooked
      ? capitalize(
          `${
            workerDeclaredEtaMessageText ?? ""
          }the worker is running late and is expected to arrive around ${formatInTimeZone(
            arrivalTime,
            workplace.timeZone,
            TIME_FORMAT
          )}`
        )
      : `${shift.worker.name} is ${formatHoursAndMinutes(
          Math.round(etaDurationInMinutes)
        )} away and is expected to arrive around ${formatInTimeZone(
          arrivalTime,
          workplace.timeZone,
          TIME_FORMAT
        )}`;
  }

  return undefined;
}

export function formatHoursAndMinutes(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  if (hours > 0 && mins > 0) {
    return `${pluralize("hour", hours, true)} ${pluralize("minute", mins, true)}`;
  }

  if (hours > 0) {
    return `${pluralize("hour", hours, true)}`;
  }

  return `${pluralize("minute", mins, true)}`;
}

// eslint-disable-next-line complexity
function getShiftChipsAndMessage(
  workplace: ShiftBoardWorkplace,
  shift: ShiftBoardShift
):
  | { chips: Array<{ status: ShiftStatus; color: Color }>; message?: string | undefined }
  | undefined {
  const isUrgentShift = shouldShowUrgentChip({
    shift: { end: shift.end.toISOString(), deleted: shift.deleted, urgency: shift.urgency },
  });

  const isWorkerAtHome = shift.trip
    ? determineIsWorkerAtHome({
        latestWorkerMovement: shift.trip?.latestWorkerMovement,
        currentEtaDistance: shift.trip?.eta.distanceInM,
        initialEtaDistance: shift.trip?.initialEta.distanceInM,
      })
    : undefined;

  const chips: Array<{ status: ShiftStatus; color: Color }> = [];

  if (shift.deleted || !shift.isBillable) {
    chips.push({ status: "Canceled", color: COLORS.GRAY });
    return { chips, message: getShiftCancelledDetails(shift, workplace) };
  }

  if (shift.verified) {
    chips.push({ status: "Shift completed", color: COLORS.LIGHT_GREEN });
    return { chips, message: undefined };
  }

  const isBookableRegularShift = shouldShowStillBookableChip({
    start: shift.start.toISOString(),
    end: shift.end.toISOString(),
    agentId: shift.worker?.id,
    deleted: shift.deleted,
    extendBookabilityTo: shift.extendBookabilityTo?.toISOString(),
    urgency: shift.urgency,
  });

  const isUrgentBookable = isUrgentShiftBookable(shift);

  const workerDeclaredEtaMessageText = isDefined(shift?.trip?.workerDeclaredEta)
    ? `${getWorkerDeclaredEtaMessageText(
        shift.trip.workerDeclaredEta,
        workplace.timeZone
      )} Currently, `
    : undefined;

  let message: string | undefined;

  if (shift.worker) {
    if (shift.clockInOut?.clockOut) {
      chips.push({ status: "Clocked out", color: COLORS.LIGHT_GREEN });
      message = `Worker clocked out at ${formatInTimeZone(
        shift.clockInOut.clockOut,
        workplace.timeZone,
        TIME_FORMAT
      )}`;
    } else if (shift.clockInOut?.clockIn) {
      chips.push({ status: "Clocked in", color: COLORS.LIGHT_GREEN });
      message = `Worker clocked in at ${formatInTimeZone(
        shift.clockInOut.clockIn,
        workplace.timeZone,
        TIME_FORMAT
      )}`;
    } else if (hasActuallyArrived(shift.trip, shift)) {
      const arrivalTime = shift.attendance?.arrivedFacilityGeofenceAt
        ? formatInTimeZone(
            shift.attendance?.arrivedFacilityGeofenceAt,
            workplace.timeZone,
            TIME_FORMAT
          )
        : undefined;
      chips.push({ status: "Arrived", color: COLORS.LIGHT_GREEN });
      message = `Worker arrived ${arrivalTime ? `at ${arrivalTime}` : ""}`;
    } else if (shift.trip && isWorkerAtHome) {
      chips.push({ status: "Worker at home", color: COLORS.BLUE });
      message = capitalize(
        `${
          workerDeclaredEtaMessageText ?? ""
        }the worker has not left home yet and is about ${formatHoursAndMinutes(
          shift.trip.initialEta.durationInMin
        )} away from the workplace`
      );
    } else if (
      shift.trip?.status === TripStatus.TRIP_PENDING &&
      shift.trip.initialEta.durationInMin === 0 &&
      shift.trip.eta.durationInMin === 0 // B2B Shift case
    ) {
      chips.push({ status: "Arrived", color: COLORS.BLUE });
    } else if (
      // Catch All for Trip Pending + If the trip status is approaching or started
      shift.trip?.status &&
      (TripStatus.TRIP_PENDING === shift.trip.status ||
        (Date.now() <= shift.end.getTime() &&
          [TripStatus.TRIP_APPROACHING, TripStatus.TRIP_STARTED].includes(shift.trip?.status)))
    ) {
      chips.push({ status: "On the way", color: COLORS.BLUE });
      message = getEtaMessageText({ shift, workplace, isUrgentShift });
    } else if (isUrgentShift && isUrgentBookable) {
      chips.push({ status: "Looking for replacement", color: COLORS.BEIGE });
      message = "Urgent shift was posted, we are looking for a replacement worker";
    } else if (isUrgentShift && shift.urgentlyBooked) {
      chips.push({ status: "On the way", color: COLORS.BLUE });
      message = "Replacement worker claimed the shift";
    } else if (shift.start.getTime() - Date.now() > 2 * 60 * 60 * 1000) {
      chips.push({ status: "Starts in 2+ hours", color: COLORS.BLUE });
    }

    if (!shift.trip && !message) {
      message = workerDeclaredEtaMessageText
        ? `${workerDeclaredEtaMessageText} we are unable to reach their device to get an exact location`
        : undefined;
      return { chips, message };
    }

    // Catch All
    if (chips.length === 0) {
      chips.push({ status: "Booked", color: COLORS.GRAY });
    }

    return { chips, message };
  }

  if (isUrgentBookable || isBookableRegularShift) {
    chips.push({ status: "Looking for worker", color: COLORS.BEIGE });
  }

  return { chips, message };
}

function useShiftCard(props: UseShiftCardProps) {
  const { shift, workplace } = props;

  const { trip, clockInOut, end, start, deleted, urgency } = shift;

  const { status, initialEta, eta } = trip ?? {};

  const isWorkerClockedIn = isDefined(clockInOut?.clockIn);
  const tripHasStarted =
    isDefined(initialEta) && isDefined(eta)
      ? initialEta.durationInMin !== eta.durationInMin
      : undefined;
  const noTripInformationAvailable = status === TripStatus.TRIP_NO_INFORMATION;

  const isShiftEnded = isAfter(new Date(), end);

  const distanceInMiles =
    isDefined(eta) && isDefined(eta.distanceInM) ? eta.distanceInM / METERS_PER_MILE : undefined;

  const isTwoHoursBeforeShiftStart = isBefore(new Date(), addHours(start, -2));
  const { chips, message } = getShiftChipsAndMessage(workplace, shift) ?? {};

  return {
    isWorkerClockedIn,
    tripHasStarted,
    noTripInformationAvailable,
    isShiftEnded,
    isTwoHoursBeforeShiftStart,
    distanceInMiles,
    workplace,
    shift,
    chips,
    message,
    isUrgentBookable: isUrgentShiftBookable(shift),
    isUrgentShift: shouldShowUrgentChip({
      shift: { end: end.toISOString(), deleted, urgency },
    }),
  };
}

export const [ShiftCardProvider, useShiftCardContext] = constate(useShiftCard);
